import { useMemo, useState } from 'react';
import { dayzColorToHex, hexToDayzColor, KIND_META, listGroups, type EditorDoc } from '../lib/model';
import { LAYER_COLORS, useDispatch, useFeatures, useStore } from '../state/store';

export type LayersPanelProps = {
  /** Zooms the map to the positions of a group. */
  onZoomToGroup: (groupId: string) => void;
  /** Opens the table editor for events.xml / types.xml. */
  onOpenTable: (docId: string) => void;
};

export function LayersPanel(props: LayersPanelProps) {
  const state = useStore();
  const { features } = useFeatures();

  const countByDoc = useMemo(() => {
    const counts = new Map<string, number>();
    for (const feature of features) counts.set(feature.docId, (counts.get(feature.docId) ?? 0) + 1);
    return counts;
  }, [features]);

  if (state.docs.length === 0) {
    return (
      <div className="panel">
        <h2>Layers</h2>
        <p className="empty">
          No files loaded. Drop your mission XML files onto the window, or load the sample project from the
          header.
        </p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2>Layers</h2>
      <ul className="layer-list">
        {state.docs.map((doc) => (
          <LayerRow key={doc.id} doc={doc} count={countByDoc.get(doc.id) ?? 0} {...props} />
        ))}
      </ul>
      <p className="hint">
        The <b>target group</b> (highlighted) receives everything you draw.
      </p>
    </div>
  );
}

function LayerRow({
  doc,
  count,
  onZoomToGroup,
  onOpenTable,
}: LayersPanelProps & { doc: EditorDoc; count: number }) {
  const state = useStore();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const groups = useMemo(() => listGroups(doc), [doc]);
  const meta = KIND_META[doc.kind];
  const isActiveDoc = state.activeDocId === doc.id;

  return (
    <li className={`layer ${isActiveDoc ? 'active' : ''}`}>
      <div className="layer-head">
        {meta.mapEditable ? (
          <input
            type="checkbox"
            checked={doc.visible}
            title="Show on map"
            onChange={(e) => dispatch({ type: 'patchDoc', docId: doc.id, patch: { visible: e.target.checked } })}
          />
        ) : (
          <span className="table-dot" title="Table editor - not drawn on the map">
            ▤
          </span>
        )}
        <button
          type="button"
          className="layer-name"
          onClick={() =>
            meta.mapEditable ? dispatch({ type: 'setActive', docId: doc.id }) : onOpenTable(doc.id)
          }
          title={meta.label}
        >
          {meta.mapEditable && <span className="swatch" style={{ background: doc.color }} />}
          {doc.fileName}
          {meta.mapEditable && <span className="muted"> · {count}</span>}
        </button>
        <button type="button" className="icon" title="Expand" onClick={() => setOpen((v) => !v)}>
          {open ? '▾' : '▸'}
        </button>
      </div>

      {open && (
        <>
          <div className="layer-tools">
            {meta.mapEditable ? (
              <>
                <select
                  value={doc.color}
                  onChange={(e) => dispatch({ type: 'patchDoc', docId: doc.id, patch: { color: e.target.value } })}
                  title="Layer colour"
                >
                  {LAYER_COLORS.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
                <label className="check" title="Locked layers cannot be edited or selected">
                  <input
                    type="checkbox"
                    checked={doc.locked}
                    onChange={(e) =>
                      dispatch({ type: 'patchDoc', docId: doc.id, patch: { locked: e.target.checked } })
                    }
                  />
                  Lock
                </label>
              </>
            ) : (
              <button type="button" onClick={() => onOpenTable(doc.id)}>
                Open table
              </button>
            )}
            <button type="button" onClick={() => dispatch({ type: 'removeDoc', docId: doc.id })}>
              Close
            </button>
          </div>

          <div className="kind-tag">{meta.label}</div>

          {meta.mapEditable && (
            <ul className="group-list">
              {groups.map((group) => (
                <li key={group.id}>
                  {group.color !== undefined && (
                    <input
                      type="color"
                      className="group-color"
                      value={dayzColorToHex(group.color)}
                      title="Territory colour, written back as 0xAARRGGBB"
                      onChange={(e) =>
                        dispatch({
                          type: 'setGroupAttr',
                          docId: doc.id,
                          groupId: group.id,
                          name: 'color',
                          value: hexToDayzColor(e.target.value),
                        })
                      }
                    />
                  )}
                  <button
                    type="button"
                    className={`group ${state.activeGroupId === group.id ? 'target' : ''}`}
                    onClick={() => dispatch({ type: 'setActive', docId: doc.id, groupId: group.id })}
                    title="Make this the target group for new positions"
                  >
                    <span className="group-label">{group.label}</span>
                    <span className="muted">{group.count}</span>
                  </button>
                  <button
                    type="button"
                    className="icon"
                    title="Zoom to this group"
                    disabled={group.count === 0}
                    onClick={() => onZoomToGroup(group.id)}
                  >
                    ⌖
                  </button>
                  <button
                    type="button"
                    className="icon"
                    title="Delete group and all of its positions"
                    onClick={() => {
                      if (confirm(`Delete "${group.label}" and its ${group.count} position(s)?`)) {
                        dispatch({ type: 'removeGroup', docId: doc.id, groupId: group.id });
                      }
                    }}
                  >
                    ✕
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="add-group"
                  onClick={() => {
                    const name = prompt(
                      doc.kind === 'eventspawns'
                        ? 'Event name (must match a name in events.xml, e.g. StaticHeliCrash)'
                        : 'Group name',
                      doc.kind === 'eventspawns' ? 'StaticHeliCrash' : 'NewGroup',
                    );
                    if (name) dispatch({ type: 'addGroup', docId: doc.id, name });
                  }}
                >
                  + {doc.kind === 'eventspawns' ? 'event' : doc.kind === 'territories' ? 'territory' : 'group'}
                </button>
              </li>
            </ul>
          )}
        </>
      )}
    </li>
  );
}
